import React, { Component } from 'react';
import { arrayOf, bool, object, func, shape, string } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { Switch, Route, withRouter } from 'react-router-dom';

import { useRouteConfiguration } from '../context/routeConfigurationContext';
import { propTypes } from '../util/types';
import * as log from '../util/log';
import { canonicalRoutePath } from '../util/routes';
import { useConfiguration } from '../context/configurationContext';

import { locationChanged } from '../ducks/routing.duck';
import { LATEST_MESSAGE_HASH, MESSAGE_HASH_PREFIX } from '../ducks/user.duck';

import { NamedRedirect } from '../components';
import NotFoundPage from '../containers/NotFoundPage/NotFoundPage';

import LoadableComponentErrorBoundary from './LoadableComponentErrorBoundary/LoadableComponentErrorBoundary';

const canShowComponent = props => {
  const { isAuthenticated, route } = props;
  const { auth } = route;
  return !auth || isAuthenticated;
};

const callLoadData = props => {
  const { match, location, route, dispatch, logoutInProgress, config } = props;
  const { loadData, name } = route;
  const shouldLoadData =
    typeof loadData === 'function' && canShowComponent(props) && !logoutInProgress;

  if (shouldLoadData) {
    dispatch(loadData(match.params, location.search, config))
      .then(() => {
        if (props.logLoadDataCalls) {
          // This gives good input for debugging issues on live environments, but with test it's not needed.
          console.log(`loadData success for ${name} route`);
        }
      })
      .catch(e => {
        log.error(e, 'load-data-failed', { routeName: name });
      });
  }
};

// ================ Not loading the same data twice ================ //
//
// Every loadData call costs Sharetribe credits. Two cases used to load a page's data again
// for nothing:
//
// 1. The first page: the server already loaded its data into __PRELOADED_STATE__, and the
//    browser loaded it all again on mount. Now the routes below reuse the server's data
//    when the server says it loaded this URL (__SSR_LOADED_URL__, server/renderer.js) and
//    the page shows no loading error. Any other route, or any doubt, loads as before.
// 2. Opening a menu or the order panel (?mobilemenu, ?mobilesearch, ?orderOpen) changed
//    the URL and so re-ran loadData. Those parameters change nothing the page loads.

// Route name => does the server-loaded state need no second try in the browser?
const REUSABLE_SSR_DATA = {
  ListingPage: state => !state.ListingPage?.showListingError,
  ListingPageCanonical: state => !state.ListingPage?.showListingError,
  ProfilePage: state => !state.ProfilePage?.userShowError && !state.ProfilePage?.queryListingsError,
  SearchPage: state => !state.SearchPage?.searchListingsError,
  CMSPage: state => !state.hostedAssets?.error,
};

// Read once: only the very first page can have server-loaded data.
let ssrLoadedUrl = typeof window !== 'undefined' ? window.__SSR_LOADED_URL__ || null : null;
const takeSsrLoadedUrl = () => {
  const url = ssrLoadedUrl;
  ssrLoadedUrl = null;
  return url;
};

const sameUrl = (a, b) => {
  try {
    return decodeURI(a) === decodeURI(b);
  } catch (e) {
    return a === b;
  }
};

export const canReuseSsrData = ({ ssrUrl, location, route, state }) => {
  const isReusable = REUSABLE_SSR_DATA[route?.name];
  return (
    !!ssrUrl &&
    !route.auth &&
    !!isReusable &&
    sameUrl(ssrUrl, `${location.pathname}${location.search}`) &&
    isReusable(state)
  );
};

const UI_ONLY_PARAMS = ['mobilemenu', 'mobilesearch', 'orderOpen'];
const searchWithoutUiParams = search => {
  const params = new URLSearchParams(search);
  UI_ONLY_PARAMS.forEach(param => params.delete(param));
  params.sort();
  return params.toString();
};

// True when the new location differs only by a menu or panel parameter.
export const onlyUiParamsChanged = (prev, next) =>
  prev.pathname === next.pathname &&
  prev.hash === next.hash &&
  prev.search !== next.search &&
  searchWithoutUiParams(prev.search) === searchWithoutUiParams(next.search);

// Hashes a page scrolls to by itself, once its data is on the page (TransactionPage).
const isPageOwnedHash = hash =>
  hash === LATEST_MESSAGE_HASH || hash.startsWith(MESSAGE_HASH_PREFIX);

export const setPageScrollPosition = (location, delayed) => {
  if (!location.hash || isPageOwnedHash(location.hash)) {
    // No hash, or one the page handles itself: scroll to top
    window.scroll({
      top: 0,
      left: 0,
    });
  } else {
    const el = document.querySelector(location.hash);
    if (el) {
      // Found element from the current page with the given fragment identifier,
      // scrolling to that element.
      //
      // NOTE: This only works on in-app navigation within the same page.
      // If smooth scrolling is needed between different pages, one needs to wait
      //   1. loadData fetch and
      //   2. code-chunk fetch
      // before making el.scrollIntoView call.

      el.scrollIntoView({
        block: 'start',
        behavior: 'smooth',
      });
    } else {
      // A naive attempt to make a delayed call to scrollIntoView
      // Note: 300 milliseconds might not be enough, but adding too much delay
      // might affect user initiated scrolling.
      delayed = window.setTimeout(() => {
        const reTry = document.querySelector(location.hash);
        // The element may still not be there (a page that loads its data first).
        // Without this check the page crashed with "Cannot read properties of null".
        if (reTry) {
          reTry.scrollIntoView({
            block: 'start',
            behavior: 'smooth',
          });
        }
      }, 300);
    }
  }
};

/**
 * Should a location change reset the scroll position to the top?
 *
 * Yes for real navigation — a new page, an in-page anchor, or a new page of
 * paginated results, which all want to start from the top.
 *
 * No when the visitor is merely refining the page they are already on: ticking a
 * filter or changing the sort order only rewrites the query string. Jumping to
 * the top there yanks them away from the control they just clicked, which on the
 * search page means losing your place in a long filter list on every tick.
 */
const shouldResetScrollPosition = (prevLocation, location) => {
  if (!prevLocation) {
    return true;
  }
  if (prevLocation.pathname !== location.pathname || prevLocation.hash !== location.hash) {
    return true;
  }
  const pageParam = l => new URLSearchParams(l.search || '').get('page');
  return pageParam(prevLocation) !== pageParam(location);
};

const handleLocationChanged = (
  dispatch,
  location,
  routeConfiguration,
  delayed,
  resetScrollPosition = true
) => {
  if (resetScrollPosition) {
    setPageScrollPosition(location, delayed);
  }
  const path = canonicalRoutePath(routeConfiguration, location);
  dispatch(locationChanged(location, path));
};

/**
 * RouteComponentRenderer handles loadData calls on client-side.
 * It also checks authentication and redirects unauthenticated users
 * away from routes that are for authenticated users only
 * (aka "auth: true" is set in routeConfiguration.js)
 *
 * This component is a container: it needs to be connected to Redux.
 */
class RouteComponentRenderer extends Component {
  componentDidMount() {
    const { dispatch, location, route, routeConfiguration } = this.props;
    this.delayed = null;
    // Calling loadData on initial rendering (on client side), unless the server already did.
    // dispatch(thunk) returns the thunk's value: here, the current state.
    const state = dispatch((_, getState) => getState());
    const reuse = canReuseSsrData({ ssrUrl: takeSsrLoadedUrl(), location, route, state });
    if (!reuse) {
      callLoadData(this.props);
    }
    handleLocationChanged(dispatch, location, routeConfiguration, this.delayed);
  }

  componentDidUpdate(prevProps) {
    const { dispatch, location, routeConfiguration } = this.props;
    // Call for handleLocationChanged affects store/state
    // and it generates an unnecessary update.
    if (prevProps.location !== this.props.location) {
      // Calling loadData after initial rendering (on client side).
      // This makes it possible to use loadData as default client side data loading technique.
      // However it is better to fetch data before location change to avoid "Loading data" state.
      if (!onlyUiParamsChanged(prevProps.location, location)) {
        callLoadData(this.props);
      }
      handleLocationChanged(
        dispatch,
        location,
        routeConfiguration,
        this.delayed,
        shouldResetScrollPosition(prevProps.location, location)
      );
    }
  }

  componentWillUnmount() {
    if (this.delayed) {
      window.clearTimeout(this.resetTimeoutId);
    }
  }

  render() {
    const { route, match, location, staticContext } = this.props;
    const { component: RouteComponent, authPage = 'SignupPage', extraProps } = route;
    const canShow = canShowComponent(this.props);
    if (!canShow) {
      staticContext.unauthorized = true;
    }
    return canShow ? (
      <LoadableComponentErrorBoundary
        routeName={route.name}
        params={match.params}
        location={location}
      >
        <RouteComponent params={match.params} location={location} {...extraProps} />
      </LoadableComponentErrorBoundary>
    ) : (
      <NamedRedirect
        name={authPage}
        state={{ from: `${location.pathname}${location.search}${location.hash}` }}
      />
    );
  }
}

RouteComponentRenderer.defaultProps = { staticContext: {} };

RouteComponentRenderer.propTypes = {
  isAuthenticated: bool.isRequired,
  logoutInProgress: bool.isRequired,
  route: propTypes.route.isRequired,
  routeConfiguration: arrayOf(propTypes.route).isRequired,
  match: shape({
    params: object.isRequired,
    url: string.isRequired,
  }).isRequired,
  location: shape({
    search: string.isRequired,
  }).isRequired,
  staticContext: object,
  dispatch: func.isRequired,
};

const mapStateToProps = state => {
  const { isAuthenticated, logoutInProgress } = state.auth;
  return { isAuthenticated, logoutInProgress };
};
const RouteComponentContainer = compose(connect(mapStateToProps))(RouteComponentRenderer);

/**
 * Routes component creates React Router rendering setup.
 * It needs routeConfiguration (named as "routes") through props.
 * Using that configuration it creates navigation on top of page-level
 * components. Essentially, it's something like:
 * <Switch>
 *   <Route render={pageA} />
 *   <Route render={pageB} />
 * </Switch>
 */
const Routes = (props, context) => {
  const routeConfiguration = useRouteConfiguration();
  const config = useConfiguration();
  const { isAuthenticated, logoutInProgress, logLoadDataCalls } = props;

  const toRouteComponent = route => {
    const renderProps = {
      isAuthenticated,
      logoutInProgress,
      route,
      routeConfiguration,
      config,
      logLoadDataCalls,
    };

    // By default, our routes are exact.
    // https://reacttraining.com/react-router/web/api/Route/exact-bool
    const isExact = route.exact != null ? route.exact : true;
    return (
      <Route
        key={route.name}
        path={route.path}
        exact={isExact}
        render={matchProps => (
          <RouteComponentContainer
            {...renderProps}
            match={matchProps.match}
            location={matchProps.location}
            staticContext={matchProps.staticContext}
          />
        )}
      />
    );
  };

  return (
    <Switch>
      {routeConfiguration.map(toRouteComponent)}
      <Route component={NotFoundPage} />
    </Switch>
  );
};

export default withRouter(Routes);
